import { toWSDateString, WSTableCell } from "@wingspanhq/fe-component-library";
import {
  InvoiceRow,
  InvoiceStatus,
  PayeeReviewStatus,
  PayerApprovalStatus
} from "@wingspanhq/wingspan-api-sdk";
import addDays from "date-fns/addDays";
import React from "react";
import { isSameDate } from "../../../../utils/dates";

type TableCellProps = React.ComponentProps<typeof WSTableCell>;

/**
 * Secondary status text shown under the invoice status pill. Rewritten for the
 * v3 search row: it reads only the raw `status`, `payer/payeeApprovalStatus`,
 * `dueDate`, and the lean `events` the row carries.
 *
 * Accepted regression (V3_MIGRATION_TRACKER.md): the v1 row's richer secondary
 * text — partial/full refund lines, instant-payout + expected-arrival estimates
 * (needed `amountDetails.memberGross` / `estimatedDepositAt`), "last viewed"
 * (`emailViewedAt`), "accepted by you on" (`memberAcceptedAt`), and the client
 * reject-comment tooltip — has no v3 row source and is dropped.
 */
export function getSecondaryTextProps(
  invoice: InvoiceRow
): TableCellProps | undefined {
  const events = invoice.events;

  switch (invoice.status) {
    case InvoiceStatus.CREATED:
      if (
        events?.createdAt &&
        events?.updatedAt &&
        isSameDate(events.createdAt, events.updatedAt)
      ) {
        return {
          secondaryText: `Created ${toWSDateString(events.createdAt, "monthDate")}`
        };
      }
      return events?.updatedAt
        ? {
            secondaryText: `Last updated ${toWSDateString(
              events.updatedAt,
              "monthDate"
            )}`
          }
        : undefined;

    case InvoiceStatus.PAYMENT_IN_TRANSIT:
      return { secondaryText: "Waiting on client's wire transfer to arrive" };

    case InvoiceStatus.PENDING:
      return { secondaryText: "Invoice is pending" };

    case InvoiceStatus.PAID_OFF_PLATFORM:
      return events?.paidAt
        ? {
            secondaryText: `Paid outside of Wingspan on ${toWSDateString(
              events.paidAt,
              "monthDate"
            )}`
          }
        : undefined;

    case InvoiceStatus.PAID:
    case InvoiceStatus.PARTIALLY_PAID:
      return events?.depositedAt
        ? {
            secondaryText: `Deposited ${toWSDateString(
              events.depositedAt,
              "monthDate"
            )}`
          }
        : undefined;

    case InvoiceStatus.OPENED:
    case InvoiceStatus.OVERDUE:
      if (invoice.payerApprovalStatus === PayerApprovalStatus.DECLINED) {
        return { secondaryText: "Client rejected invoice" };
      }
      if (invoice.payeeReviewStatus === PayeeReviewStatus.RESUBMITTED) {
        return { secondaryText: "Client resolved dispute" };
      }
      if (invoice.status === InvoiceStatus.OVERDUE && invoice.dueDate) {
        return {
          secondaryText: `Overdue (${toWSDateString(invoice.dueDate, "monthDate")})`,
          secondaryTextColor: "red400"
        };
      }
      if (invoice.dueDate && addDays(new Date(), 3) > invoice.dueDate) {
        return {
          secondaryText: `Due soon (${toWSDateString(invoice.dueDate, "monthDate")})`,
          secondaryTextColor: "amber400"
        };
      }
      return events?.openedAt
        ? {
            secondaryText: `Sent ${toWSDateString(events.openedAt, "monthDate")}`
          }
        : undefined;

    default:
      return undefined;
  }
}
